<template>
    <base-panel
        :panel="panel"
        :review-mode="reviewMode"
        v-on:continue="handleContinue()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        ref="basePanel"
    >
        <template slot="content">
            <template v-if="panel.panel_type == 'singleselect'">
                <div v-for="choice in choices" :key="choice.id" class="mb-2">
                    <div class="custom-control custom-radio global-theme-text mb-1">
                        <input
                            v-model="singleSelectChoiceId"
                            @change="checkChoice($event, choice)"
                            :value="choice.id"
                            type="radio"
                            class="custom-control-input"
                            :disabled="choice.showLoading || locked"
                            :id="'radio' + choice.id"
                        />
                        <label
                            class="custom-control-label choice-label global-theme-text ml-1"
                            :for="'radio' + choice.id"
                        >
                            <span class="whitespace-pre mr-1">{{ choice.text }}</span>
                            <span
                                v-show="choice.showLoading"
                                class="spinner-grow spinner-grow-sm text-primary ml-loader pt-1"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            <span
                                v-if="
                                    showChoiceFeedback(choice) &&
                                    showChoiceCorrectness &&
                                    choice.correct &&
                                    !choice.showLoading
                                "
                                class="icon icon-lg icon-glyphicons-basic-circle-empty-check feedback-icon align-middle text-success d-inline rounded-circle correct-icon-animate"
                                aria-label="Correct selection"
                            ></span>
                            <span
                                v-if="
                                    showChoiceFeedback(choice) &&
                                    showChoiceCorrectness &&
                                    choice.correct === false &&
                                    !choice.showLoading
                                "
                                class="icon icon-lg icon-glyphicons-basic-circle-empty-remove feedback-icon align-middle text-danger d-inline-block incorrect-icon-animate"
                                aria-label="Incorrect selection"
                            ></span>
                        </label>
                        <p
                            v-show="showChoiceFeedback(choice) && choice.message"
                            class="text-secondary whitespace-pre mb-2 ml-1"
                            dir="auto"
                        >{{ choice.message }}</p>
                    </div>
                </div>
            </template>
            <template v-if="panel.panel_type == 'multiselect'">
                <div v-for="choice in choices" :key="choice.id" class="mb-2">
                    <div class="custom-control custom-checkbox global-theme-text mb-1">
                        <input
                            v-model="choice.checked"
                            @change="checkChoice($event, choice)"
                            type="checkbox"
                            class="custom-control-input"
                            :disabled="choiceIsDisabled(choice)"
                            :id="'checkbox' + choice.id"
                        />
                        <label
                            class="custom-control-label choice-label global-theme-text ml-1"
                            :for="'checkbox' + choice.id"
                        >
                            <div class="text-body d-inline" :class="{ 'mr-1': choice.checked }">
                                {{ choice.text }}
                            </div>
                            <span
                                v-show="choice.showLoading"
                                class="spinner-grow spinner-grow-sm text-primary ml-loader pt-1"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            <span
                                v-if="
                                    showChoiceFeedback(choice) &&
                                    showChoiceCorrectness &&
                                    choice.correct &&
                                    !choice.showLoading
                                "
                                class="icon icon-lg icon-glyphicons-basic-circle-empty-check feedback-icon align-middle text-success d-inline rounded-circle correct-icon-animate"
                       
                                aria-label="Correct selection"
                            ></span>
                            <span
                                v-if="
                                    showChoiceFeedback(choice) &&
                                    showChoiceCorrectness &&
                                    choice.correct === false &&
                                    !choice.showLoading
                                "
                                class="icon icon-lg icon-glyphicons-basic-circle-empty-remove feedback-icon align-middle text-danger d-inline-block incorrect-icon-animate"
                                aria-label="Incorrect selection"
                            ></span>
                        </label>
                        <p
                            v-show="choice.message && showChoiceFeedback(choice)"
                            class="text-secondary whitespace-pre mb-2 mt-1 ml-1"
                            dir="auto"
                        >{{ choice.message }}</p>
                    </div>
                </div>
            </template>
        </template>
        <template slot="footer"> </template>
    </base-panel>
</template>

<script>
import http from "@/axios.js"
import BasePanel from "@/components/panels/BasePanel"

export default {
    name: "ChoicePanel",
    components: {
        BasePanel,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        sessionId: {
            type: [String, Number],
            required: false,
        },
        locked: {
            type: Boolean,
            required: false,
        },
        reviewMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            // The ID of the single select choice that's currently selected.
            singleSelectChoiceId: null,
            // The ID of the single select choice that has feedback loaded. This property allows us
            // to control when feedback messages are displayed, for smoother loading transitions when
            // the user is selecting choices.
            singleSelectFeedbackLoadedId: null,
            lastVitalsTriggers: [],
        }
    },
    computed: {
        panelID() {
            return this.panel.id
        },
        choices() {
            return this.panel.choices
        },
        choicesCount() {
            if (this.panel.panel_type == "singleselect") {
                return this.singleSelectChoiceId ? 1 : 0
            } else {
                return this.choices.filter((choice) => choice.checked).length
            }
        },
        correctChoicesCount() {
            if (this.panel.panel_type == "singleselect") {
                let vm = this
                let selectedChoice = this.choices.find((c) => c.id == vm.singleSelectChoiceId)
                return selectedChoice && selectedChoice.correct ? 1 : 0
            } else {
                return this.choices.filter((choice) => choice.correct && choice.checked).length
            }
        },
        choiceResponsesChecked() {
            if (this.panel.panel_type == "singleselect") {
                let selectedChoice = this.panel.choices.find((c) => c.id == this.singleSelectChoiceId)
                if (!selectedChoice) {
                    return []
                }
                return [selectedChoice]
            } else {
                return this.panel.choices.filter((c) => c.checked)
            }
        },
        savedResponseCount() {
            return this.savedResponses ? this.savedResponses.length : 0
        },
        isSubmitted() {
            return this.savedResponseCount > 0
        },
        showChoiceCorrectness() {
            if (this.reviewMode) {
                // Always show correctness when in review mode.
                return true
            }
            return this.panel.show_correctness && (this.isSubmitted || this.panel.record_selections_instantly)
        },
    },
    watch: {
        choiceResponsesChecked(val) {
            this.$emit("update:choiceResponses", val)
        },
        choicesCount(val) {
            this.$emit("update:choicesCount", val)
        },
        correctChoicesCount(val) {
            this.$emit("update:correctChoicesCount", val)
        },
        panelID() {
            this.populateSavedResponses()
        },
        savedResponseCount() {
            this.populateSavedResponses()
        },
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
        },
    },
    methods: {
        populateSavedResponses() {
            if (this.savedResponses && this.savedResponses.length) {
                let vm = this
                // Update multiple selection panels with choice responses.
                this.savedResponses.forEach(function (choiceResponse) {
                    // Find the choice.
                    let choice = vm.choices.find((choice) => choice.id == choiceResponse.choice.id)

                    if (!choice) {
                        return
                    }

                    // Set the 'checked' state of the select.
                    vm.$set(choice, "checked", choiceResponse.is_selected)

                    if (choiceResponse.is_selected && vm.panel.panel_type == "singleselect") {
                        // Update the selected choice ID for the single selection.
                        vm.singleSelectChoiceId = choiceResponse.choice.id
                        vm.singleSelectFeedbackLoadedId = choiceResponse.choice.id
                    }

                    // Set the correctness and feedback message on the choice.
                    vm.$set(choice, "message", choiceResponse.choice.message)
                    vm.$set(choice, "correct", choiceResponse.choice.correct)
                })
                this.$emit("update:isSubmitted", true)
            }
        },
        choiceIsDisabled(choice) {
            if (choice.showLoading || this.locked) {
                return true
            }
            if (choice.checked && !this.panel.allow_deselecting_multi) {
                return true
            }
            return false
        },
        // Handles a check-type choice selection.
        checkChoice(event, choice) {
            if (this.locked || !this.sessionId) {
                return
            }

            if (Object.hasOwn(choice, "correct")) {
                // The choice already has feedback loaded, so display feedback for the choice
                // right away.
                this.singleSelectFeedbackLoadedId = choice.id
            }

            // Prevent recording the choice if instant feedback is disabled on the panel. Once the
            // inquiry has been submitted, show instant feedback on further choices.
            if (!this.panel.record_selections_instantly && !this.isSubmitted) {
                return
            }

            if (choice.correct === undefined) {
                this.$set(choice, "showLoading", true)
            }

            const choiceState = event.target.checked
            let vm = this
            http.post(`/api/students/sessions/${this.sessionId}/choice/${choice.id}/`, {
                state: choiceState,
            }).then(function (response) {
                let selectedChoice = response.data.choice
                vm.singleSelectFeedbackLoadedId = selectedChoice.id

                // Set the correctness property on the choice.
                vm.$set(choice, "correct", selectedChoice.correct)
                // Display the message below the choice.
                vm.$set(choice, "message", selectedChoice.message)

                choice.showLoading = false
                vm.correctAnswers = response.data.correct_responses_visible

                // If panel type is multiple choice, only one choice can be active at a time, so
                // deactivate previous vitals triggers.
                if (vm.panel.panel_type === "singleselect") {
                    vm.$emit("trigger-vitals", { triggers: vm.lastVitalsTriggers, activate: false })
                }

                // Activate or deactivate vitals triggers included with the payload.
                let vitalsTriggers = response.data.choice.vitals_triggers
                if (vitalsTriggers.length > 0) {
                    vm.$emit("trigger-vitals", { triggers: vitalsTriggers, activate: choiceState })
                    vm.lastVitalsTriggers = vitalsTriggers
                }

                vm.$emit("response-received")
            })
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
        handleUpdateWidth($event) {
            this.$emit("update-width", $event)
        },
        skipVideos() {
            this.$refs.basePanel.skipVideos()
        },
        // Returns true if feedback should be shown for a given choice.
        showChoiceFeedback(choice) {
            if (!this.panel.record_selections_instantly && !this.isSubmitted) {
                return false
            } else if (this.reviewMode) {
                // In review mode, show feedback even if the choice was unchecked.
                return choice.checked !== undefined
            } else if (this.panel.panel_type == "singleselect") {
                return this.singleSelectFeedbackLoadedId == choice.id
            } else {
                return choice.checked
            }
        },
    },
    mounted() {
        this.populateSavedResponses()
    },
}
</script>

<style scoped lang="scss">
.choice-label {
    line-height: 1.55;
}

.feedback-icon {
    width: 16px;
    margin-bottom: 1px;
}

.ml-loader {
    margin-left: 2px;
}
</style>
